import { useRef, useState, useEffect, Fragment, forwardRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { trans } from '@spotahome/soyuz/client';

import {
  getComponentToRender,
  getLiteralToDisplay
} from './utils/getSliderComponent';
import {
  getNextItemIndex,
  getPreviousItemIndex
} from './utils/sliderIndexCalculator';
import { ITEM_WIDTH, LIMIT_TO_SWIPE, KEYS } from './utils/constants';
import {
  hasEndedSwipeAndMustStay,
  hasEndedSwipeAndMustContinue,
  hasEndedNavigation
} from './utils/sliderConditions';

import GalleryArrow from './GalleryArrow';

import './SliderTrack.scss';

const USE_CURRENT_INDEX = true;

const getInitialState = (currentIndex = 0) => ({
  enteringItemIndex: null,
  enteringItemPosition: null,
  position: 0,
  isTransitioning: false,
  isSwiping: false,
  currentIndex
});

const getEnteringItem = (index, position) => ({
  enteringItemIndex: index,
  enteringItemPosition: position
});

function SliderTrack({ type, items, currentTrackItem, counterRef }) {
  const currentImageRef = useRef(null);
  const [state, setState] = useState(getInitialState(currentTrackItem));
  const initialPosition = useRef(0);
  const prevStateRef = useRef(state);

  useEffect(() => {
    setState(getInitialState(currentTrackItem));
  }, [currentTrackItem]);

  const getNextEnteringItem = prevState => {
    const nextIndex = getNextItemIndex(prevState.currentIndex, items.length);
    return getEnteringItem(nextIndex, ITEM_WIDTH);
  };

  const getPrevEnteringItem = prevState => {
    const prevIndex = getPreviousItemIndex(
      prevState.currentIndex,
      items.length
    );
    return getEnteringItem(prevIndex, -ITEM_WIDTH);
  };

  const handleNextItem = e => {
    e?.preventDefault();
    setState(prevState => ({
      ...prevState,
      ...getNextEnteringItem(prevState)
    }));
  };

  const handlePreviousItem = e => {
    e?.preventDefault();
    setState(prevState => ({
      ...prevState,
      ...getPrevEnteringItem(prevState)
    }));
  };

  useEffect(() => {
    const handleKeyboardPress = e => {
      if (e.keyCode === KEYS.NEXT_ARROW) {
        handleNextItem(e);
      } else if (e.keyCode === KEYS.PREV_ARROW) {
        handlePreviousItem(e);
      }
    };
    document.addEventListener('keydown', handleKeyboardPress);
    return () => {
      document.removeEventListener('keydown', handleKeyboardPress);
    };
    // eslint-disable-next-line
  }, []);

  const updatePositions = () => {
    setState(prevState => ({
      ...prevState,
      position: -1 * prevState.enteringItemPosition,
      isTransitioning: true,
      isSwiping: false
    }));
  };

  const resetInitialStateWithIndex = useCurrent => {
    setState(prevState => {
      const indexToUse = useCurrent
        ? prevState.currentIndex
        : prevState.enteringItemIndex;
      return getInitialState(indexToUse);
    });
  };

  const handleEndTransition = () => {
    setState(prevState => ({
      ...prevState,
      isTransitioning: false
    }));
  };

  useEffect(() => {
    if (hasEndedSwipeAndMustStay(prevStateRef.current, state)) {
      resetInitialStateWithIndex(USE_CURRENT_INDEX);
    }
    if (hasEndedSwipeAndMustContinue({}, state)) {
      resetInitialStateWithIndex(false);
    }
    if (hasEndedNavigation({}, state)) {
      updatePositions();
    }

    prevStateRef.current = state;
    // eslint-disable-next-line
  }, [state.isTransitioning, state.isSwiping, state.position]);

  const getItemPosition = () => {
    return currentImageRef.current?.offsetLeft || 0;
  };

  const getItemWidth = () => {
    return currentImageRef.current?.clientWidth || ITEM_WIDTH;
  };

  const handleTouchStart = e => {
    initialPosition.current = getItemPosition() - e.touches[0].clientX;
  };

  const handleTouchMove = e => {
    const touchMove = initialPosition.current + e.touches[0].clientX;
    const itemWidth = getItemWidth();
    let enteringItemInfo = {};
    let newState = {
      ...state,
      isSwiping: true,
      isTransitioning: false,
      position: (touchMove * ITEM_WIDTH) / itemWidth
    };

    if (newState.position > ITEM_WIDTH) {
      initialPosition.current -= itemWidth;
      newState = {
        ...newState,
        position: newState.position - ITEM_WIDTH,
        currentIndex: state.enteringItemIndex
      };
      enteringItemInfo = getPrevEnteringItem(newState);
    } else if (newState.position < -ITEM_WIDTH) {
      initialPosition.current += itemWidth;
      newState = {
        ...newState,
        position: newState.position + ITEM_WIDTH,
        currentIndex: state.enteringItemIndex
      };
      enteringItemInfo = getNextEnteringItem(newState);
    } else if (newState.position > 0) {
      enteringItemInfo = getPrevEnteringItem(state);
    } else if (newState.position < 0) {
      enteringItemInfo = getNextEnteringItem(state);
    }

    setState({
      ...newState,
      ...enteringItemInfo
    });
  };

  const handleTouchEnd = () => {
    if (Math.abs(state.position) >= LIMIT_TO_SWIPE) {
      updatePositions();
    } else {
      setState(prevState => ({
        ...prevState,
        position: 0,
        isTransitioning: true
      }));
    }
  };

  const getItemStyle = left => ({ left: `${left}%` });

  const isIdle = () => !state.isTransitioning;

  const renderEnteringItem = ComponentToRender =>
    state.enteringItemIndex !== null && (
      <span
        className="slider-track__entering-item"
        style={getItemStyle(state.enteringItemPosition)}
      >
        <ComponentToRender
          key="enteringItem"
          item={items[state.enteringItemIndex]}
        />
      </span>
    );

  const ComponentToRender = getComponentToRender(type);
  const currentItem = items[state.currentIndex];

  const trackClassName = classNames('slider-track__track', {
    'slider-track__track--animating': !isIdle()
  });

  return (
    <Fragment>
      <div className="slider-track__wrap">
        <GalleryArrow navRole="prev" onClick={handlePreviousItem} />
        <div
          ref={currentImageRef}
          className={trackClassName}
          onTransitionEnd={handleEndTransition}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={getItemStyle(state.position)}
        >
          <ComponentToRender key="currentItem" item={currentItem} />
          {renderEnteringItem(ComponentToRender)}
        </div>
        <GalleryArrow navRole="next" onClick={handleNextItem} />
      </div>
      <div ref={counterRef} className="slider-track__counter">
        {state.currentIndex + 1}/{items.length}{' '}
        {trans(getLiteralToDisplay(type))}
      </div>
    </Fragment>
  );
}

SliderTrack.propTypes = {
  type: PropTypes.oneOf(['img', 'plan', 'video', 'map']).isRequired,
  items: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  currentTrackItem: PropTypes.number.isRequired,
  counterRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.object })
  ]).isRequired
};

export default forwardRef((props, ref) => (
  <SliderTrack {...props} counterRef={ref} />
));
